import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Clapperboard, Plus } from "lucide-react";

const ease = [0.16, 1, 0.3, 1];

// Monochrome text logotypes — premium editorial treatment
const LOGOS = [
  { name: "IMDb", cls: "font-display font-black text-2xl tracking-tight" },
  { name: "SONY LIV", cls: "font-display font-extrabold text-lg tracking-[0.08em]" },
  { name: "NETFLIX", cls: "font-display font-black text-xl tracking-[0.14em]" },
  { name: "prime video", cls: "font-display font-bold text-xl lowercase tracking-tight" },
  { name: "ZEE STUDIOS", cls: "font-display font-extrabold text-lg tracking-[0.1em]" },
  { name: "Disney+ Hotstar", cls: "font-display font-bold text-lg tracking-tight" },
  { name: "DD NATIONAL", cls: "font-display font-bold text-lg tracking-[0.12em]" },
];

const TICKER_ITEMS = [
  "Gadar 2", "Leo", "The Vaccine War", "Garmi", "Abhay S3", "Jamtara S2", "Dahaad S2", "Ginny Weds Sunny 2",
];

export const FeaturedOn = () => (
  <section data-testid="featured-on" className="bg-[#F8F6F2] py-[90px] border-b border-[#E5E7EB]">
    <div className="max-w-[1320px] mx-auto px-6 md:px-10 text-center">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.7, ease }}
      >
        <p className="font-mono text-[14px] tracking-[0.3em] uppercase text-[#6B7280]">Featured On</p>
        <h2 className="mt-4 font-serif text-[30px] md:text-[38px] xl:text-[56px] leading-[1.1] text-[#111827]">
          Trusted Across Films, OTT &<br className="hidden md:block" /> National Productions
        </h2>
        <motion.span
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3, ease }}
          className="mt-6 mx-auto block w-16 h-[2px] bg-[#4F6D8A] origin-center"
        />
      </motion.div>

      {/* Logo wall */}
      <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6">
        {LOGOS.map((l, i) => (
          <motion.div
            key={l.name}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-30px" }}
            transition={{ duration: 0.5, delay: i * 0.06, ease }}
            data-testid={`featured-logo-${l.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className="bg-white rounded-[22px] border border-[#E5E7EB] shadow-[0_8px_30px_-12px_rgba(15,42,67,0.12)] py-8 px-4 flex items-center justify-center hover:-translate-y-1 hover:shadow-[0_20px_45px_-15px_rgba(15,42,67,0.25)] transition-all duration-300 group cursor-default"
          >
            <span className={`${l.cls} text-[#111827]/45 group-hover:text-[#111827] transition-colors duration-300`}>
              {l.name}
            </span>
          </motion.div>
        ))}
        {/* And More card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-30px" }}
          transition={{ duration: 0.5, delay: 7 * 0.06, ease }}
          data-testid="featured-logo-and-more"
          className="bg-white rounded-[22px] border border-dashed border-[#4F6D8A]/40 py-8 px-4 flex items-center justify-center gap-2 hover:-translate-y-1 hover:shadow-[0_20px_45px_-15px_rgba(15,42,67,0.25)] transition-all duration-300 group cursor-default"
        >
          <Plus size={15} strokeWidth={1.5} className="text-[#4F6D8A]/60 group-hover:text-[#4F6D8A] transition-colors duration-300" />
          <span className="font-mono text-[13px] tracking-[0.25em] uppercase text-[#6B7280] group-hover:text-[#0F2A43] transition-colors duration-300">
            And More…
          </span>
        </motion.div>
      </div>

      {/* Cinematic ticker */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-30px" }}
        transition={{ duration: 0.6, delay: 0.15, ease }}
        className="mt-12"
        data-testid="featured-ticker"
      >
        <div className="bg-[#0F2A43] rounded-full py-4 overflow-hidden shadow-[0_20px_50px_-15px_rgba(15,42,67,0.45)]">
          <Marquee speed={35} gradient={false} pauseOnHover>
            {TICKER_ITEMS.map((t) => (
              <span key={t} className="mx-7 inline-flex items-center gap-3 font-mono text-[12px] tracking-[0.25em] uppercase text-white/85">
                <Clapperboard size={12} strokeWidth={1.5} className="text-[#8FAECC]" />
                {t}
              </span>
            ))}
          </Marquee>
        </div>
      </motion.div>
    </div>
  </section>
);
